import { AuditLogScopeType, ResourceType } from "@common/types/auditLog";
import { APIProcessName } from "../process-names";
import { HistoryField, HistoryGroupBy } from "./history-fields";
import type {
    DiffValueHistorySourceTypeConfig,
    HistorySourceTypeConfig,
    ResourceTypeConfig,
    SourceTypeTemplate,
    StandardHistorySourceTypeConfig,
} from "./history-source-config";

export interface DerivedHistorySourceTypeConfigs {
    sourceTypeConfigs: readonly HistorySourceTypeConfig[];
    sourceTypeWhitelist: readonly string[];
    groupedSourceTypes: readonly string[];
    nonGroupedSourceTypes: readonly string[];
    sourceGroupedSourceTypes: readonly string[];
    sourceGroupedConfigs: readonly HistorySourceTypeConfig[];
    diffValueGroupedSourceTypes: readonly string[];
    diffValueGroupedConfigs: readonly DiffValueHistorySourceTypeConfig[];
    sourceCountDistinctConfigs: readonly StandardHistorySourceTypeConfig[];
    resourceTypeWhitelist: readonly ResourceType[];
    fieldWhitelist: readonly HistoryField[];
    getResourceTypeConfigsForSource: (
        sourceType: string,
    ) => ResourceTypeConfig[];
}

const DEFAULT_HISTORY_SCOPES: readonly AuditLogScopeType[] = [
    AuditLogScopeType.WORKSPACE,
];

export const ALL_HISTORY_SCOPES: readonly AuditLogScopeType[] = [
    AuditLogScopeType.WORKSPACE,
    AuditLogScopeType.ORGANIZATION,
];

export function deriveHistorySourceTypeConfigs(
    configs: readonly HistorySourceTypeConfig[],
    scope: AuditLogScopeType = AuditLogScopeType.WORKSPACE,
): DerivedHistorySourceTypeConfigs {
    const scopedConfigs = configs.filter((config) =>
        (config.scopes ?? DEFAULT_HISTORY_SCOPES).includes(scope),
    );
    const enabledConfigs = scopedConfigs.filter((config) => config.enabled);
    const enabledBySourceType = new Map(
        enabledConfigs.map((config) => [config.sourceType, config]),
    );

    const sourceTypeWhitelist = enabledConfigs.map(
        (config) => config.sourceType,
    );
    const groupedSourceTypes = enabledConfigs
        .filter((config) => config.groupBy === HistoryGroupBy.FIELD)
        .map((config) => config.sourceType);
    const nonGroupedSourceTypes = enabledConfigs
        .filter((config) => config.groupBy === HistoryGroupBy.NONE)
        .map((config) => config.sourceType);
    const sourceGroupedConfigs = enabledConfigs.filter(
        (config) => config.groupBy === HistoryGroupBy.SOURCE,
    );
    const sourceGroupedSourceTypes = sourceGroupedConfigs.map(
        (config) => config.sourceType,
    );
    const diffValueGroupedConfigs = enabledConfigs.filter(
        (config): config is DiffValueHistorySourceTypeConfig =>
            config.groupBy === HistoryGroupBy.DIFF_VALUE,
    );
    const diffValueGroupedSourceTypes = diffValueGroupedConfigs.map(
        (config) => config.sourceType,
    );
    const sourceCountDistinctConfigs = enabledConfigs.filter(
        (config): config is StandardHistorySourceTypeConfig =>
            config.groupBy === HistoryGroupBy.SOURCE_COUNT_DISTINCT,
    );
    const resourceTypeWhitelist = [
        ...new Set(
            scopedConfigs.flatMap((config) =>
                config.resourceTypeConfigs.map(
                    (rtConfig) => rtConfig.resourceType,
                ),
            ),
        ),
    ];
    const fieldWhitelist = [
        ...new Set(
            scopedConfigs.flatMap((config) =>
                config.resourceTypeConfigs.flatMap(
                    (rtConfig) => rtConfig.fields,
                ),
            ),
        ),
    ];

    return {
        sourceTypeConfigs: scopedConfigs,
        sourceTypeWhitelist,
        groupedSourceTypes,
        nonGroupedSourceTypes,
        sourceGroupedSourceTypes,
        sourceGroupedConfigs,
        diffValueGroupedSourceTypes,
        diffValueGroupedConfigs,
        sourceCountDistinctConfigs,
        resourceTypeWhitelist,
        fieldWhitelist,
        getResourceTypeConfigsForSource: (sourceType: string) =>
            enabledBySourceType.get(sourceType)?.resourceTypeConfigs ?? [],
    };
}

const ORGANIZATION_MEMBER_FIELDS: HistoryField[] = [
    HistoryField.USER_ID,
    HistoryField.ROLE,
    HistoryField.ASSIGN_TO_NEW_CLIENTS,
    HistoryField.CLIENT_WORKSPACE_IDS,
];

const ORGANIZATION_INVITE_FIELDS: HistoryField[] = [
    HistoryField.EMAIL,
    HistoryField.ROLE,
    HistoryField.ASSIGN_TO_NEW_CLIENTS,
    HistoryField.CLIENT_WORKSPACE_IDS,
];

const ORGANIZATION_ONLY_SCOPES: readonly AuditLogScopeType[] = [
    AuditLogScopeType.ORGANIZATION,
];

export const organizationMemberSources: HistorySourceTypeConfig[] = [
    {
        sourceType: APIProcessName.INVITE_MEMBER,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.ORGANIZATION_INVITE,
                fields: ORGANIZATION_INVITE_FIELDS,
            },
            {
                resourceType: ResourceType.ORGANIZATION_MEMBER,
                fields: ORGANIZATION_MEMBER_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
        scopes: ORGANIZATION_ONLY_SCOPES,
    },
    {
        sourceType: APIProcessName.USER_ONBOARDING_REGISTER,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.ORGANIZATION_MEMBER,
                fields: ORGANIZATION_MEMBER_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
        scopes: ORGANIZATION_ONLY_SCOPES,
    },
    {
        sourceType: APIProcessName.UPDATE_WORKSPACE_ASSIGNMENT,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.ORGANIZATION_MEMBER,
                fields: ORGANIZATION_MEMBER_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
        scopes: ORGANIZATION_ONLY_SCOPES,
    },
    {
        sourceType: APIProcessName.REMOVE_MEMBER,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.ORGANIZATION_MEMBER,
                fields: ORGANIZATION_MEMBER_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
        scopes: ORGANIZATION_ONLY_SCOPES,
    },
    {
        sourceType: APIProcessName.TRANSFER_ORGANIZATION_OWNER,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.ORGANIZATION_MEMBER,
                fields: ORGANIZATION_MEMBER_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
        scopes: ORGANIZATION_ONLY_SCOPES,
    },
];

export const ORGANIZATION_MEMBER_SOURCE_TYPE_TEMPLATES: Record<
    string,
    SourceTypeTemplate
> = {
    [APIProcessName.INVITE_MEMBER]: {
        verb: "Added",
        noun: "member",
        resourceTypeOverrides: {
            [ResourceType.ORGANIZATION_INVITE]: {
                verb: "Invited",
                noun: "member",
            },
        },
    },
    [APIProcessName.USER_ONBOARDING_REGISTER]: {
        verb: "Added",
        noun: "member",
    },
    [APIProcessName.UPDATE_WORKSPACE_ASSIGNMENT]: {
        verb: "Updated",
        noun: "member",
    },
    [APIProcessName.REMOVE_MEMBER]: {
        verb: "Removed",
        noun: "member",
    },
    [APIProcessName.TRANSFER_ORGANIZATION_OWNER]: {
        verb: "Updated",
        noun: "member",
    },
};
